import React, { useEffect, useState } from "react";
import { Plus } from "@phosphor-icons/react";
import api from "@/lib/api";
import { NotifyBuyerBox } from "@/components/NotifyBuyerBox";
import { TYPE_META } from "./destinationConstants";
import WebhookFields from "./fields/WebhookFields";
import EmailFields from "./fields/EmailFields";
import BearerCrmFields from "./fields/BearerCrmFields";
import ZohoFields from "./fields/ZohoFields";
import PipedriveFields from "./fields/PipedriveFields";
import ZapierFields from "./fields/ZapierFields";
import { LawRulerFields, LeadDocketFields } from "./fields/LegalCrmFields";
import { S3Fields, FtpFields } from "./fields/StorageFields";
import StateAllowlistFields from "@/components/shared/StateAllowlistFields";
import { normalizeStateInput } from "@/components/shared/StateCheckboxSelector";
import BuyerCombobox from "@/components/shared/BuyerCombobox";
import CampaignPicker from "@/components/shared/CampaignPicker";
import AffiliateMultiSelect from "@/components/shared/AffiliateMultiSelect";
const BEARER_CRMS = new Set(["crm_salesforce", "crm_litify", "crm_lawmatics", "crm_hubspot"]);

/**
 * Render the per-type configuration block. Adding a new destination
 * type = add a case here and a small `<Type>Fields.jsx` under `fields/`.
 */
export function TypeSpecificFields({ type, config, setConfig, extraPlace = false }) {
  if (type === "webhook") return <WebhookFields config={config} setConfig={setConfig} />;
  if (type === "email") return <EmailFields config={config} setConfig={setConfig} extraPlace={extraPlace} />;
  if (BEARER_CRMS.has(type)) return <BearerCrmFields type={type} config={config} setConfig={setConfig} />;
  if (type === "crm_zoho") return <ZohoFields config={config} setConfig={setConfig} />;
  if (type === "crm_pipedrive") return <PipedriveFields config={config} setConfig={setConfig} />;
  if (type === "crm_zapier") return <ZapierFields config={config} setConfig={setConfig} />;
  if (type === "crm_lawruler") return <LawRulerFields config={config} setConfig={setConfig} />;
  if (type === "crm_leaddocket") return <LeadDocketFields config={config} setConfig={setConfig} />;
  if (type === "s3") return <S3Fields config={config} setConfig={setConfig} />;
  if (type === "ftp") return <FtpFields config={config} setConfig={setConfig} />;
  return null;
}

/**
 * The "Create destination" form. Delegates the per-type config block to a
 * dedicated `<Type>Fields.jsx` sub-component under `./fields/`.
 *
 * Iter-61: state allowlist + per-state fees are now hoisted here (via the
 * shared `StateAllowlistFields`) so every destination type — email,
 * webhook, all 8 CRMs, Zapier, S3, FTP — gets the same checkbox UI.
 * Legacy comma-separated string values load correctly (see
 * `normalizeStateInput` in `StateCheckboxSelector.jsx`).
 */
export default function DestinationForm({
  form, setForm, filteredCampaigns, onSubmit,
  // Iter-61.3 — edit-mode props. When `editingId` is set the form
  // header, submit button label and read-only fields switch to
  // "Edit destination" semantics. When null/undefined the form
  // behaves exactly as it did before (Create Destination).
  mode = "create",
  editingId = null,
  editingName = null,
  // Iter-61.4.1 — parent passes the current buyer name so the
  // Edit view can render it without loading the whole Client
  // directory. `null` on Create.
  editingClientName = null,
  onCancel,
  notify = null,
  setNotify = null,
  notifyPreview = null,
}) {
  // Iter-61.4.2 — the parent used to prefetch every campaign and
  // filter locally by client_id. That preloaded 500 cap silently
  // lost campaigns for buyers with many campaigns. The new
  // CampaignPicker below queries scoped to `form.client_id` so
  // `filteredCampaigns` is accepted for backwards-compatibility but
  // no longer required.
  void filteredCampaigns;
  // Iter-61.2 — MUST use React's functional updater form. Multiple
  // config field writes coming from the shared state-selector
  // (state_allowlist + state_fees_text) can arrive in the same tick;
  // the previous `setForm({...form, ...})` closure captured a stale
  // `form` and the second write silently overwrote the first (e.g.
  // clicking a state checkbox appeared to do nothing because the
  // state_fees_text write reverted `state_allowlist` to its pre-click
  // value). The functional updater always reads `prev` from React so
  // sequential writes compose correctly.
  const setConfig = (k, v) =>
    setForm((prev) => ({ ...prev, config: { ...prev.config, [k]: v } }));

  // Iter-61: when a client is selected, load their intake_criteria to
  // narrow the destination override picker. `null` = no buyer restriction
  // (platform-wide destination) so the checkbox UI allows any state.
  const [buyerAcceptedStates, setBuyerAcceptedStates] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (!form.client_id) { setBuyerAcceptedStates(null); return () => {}; }
    (async () => {
      try {
        const { data } = await api.get(`/organizations/${form.client_id}`);
        if (cancelled) return;
        const accepted = data?.intake_criteria?.accepted_states;
        setBuyerAcceptedStates(
          Array.isArray(accepted) && accepted.length ? accepted : null,
        );
      } catch {
        if (!cancelled) setBuyerAcceptedStates(null);
      }
    })();
    return () => { cancelled = true; };
  }, [form.client_id]);

  /**
   * Iter-62.1.3 — Track the currently-selected Buyer's display name
   * alongside `form.client_id` so the visible label always matches the
   * underlying id in Create/Clone mode.
   *
   * Manual acceptance test 16 failure:
   *   Clone from a source destination owned by Buyer A, then pick
   *   Buyer B from the combobox. Parent state kept `editingClientName`
   *   at "Buyer A" (parent only sets it on Clone/Edit open, never on
   *   user selection). `BuyerCombobox` therefore received
   *   `value={id:B.id, name:""}` + `initialName="Buyer A"` — its
   *   display-priority (`value.name || initialName || resolvedName`)
   *   fell through to the stale name.
   *
   * Fix: keep a local `buyerLabel` in the form, initialised from the
   * parent's `editingClientName`, and updated in the `onChange`
   * handler with `v.name`. The prop `editingClientName` becomes the
   * seed / authoritative reset (via useEffect), never the render-time
   * source of truth. Result: visible label is always paired with
   * `form.client_id`, whether the parent seeded it or the user
   * picked it.
   */
  const [buyerLabel, setBuyerLabel] = useState(editingClientName || "");
  useEffect(() => {
    // Re-sync when the parent authoritatively provides a new label —
    // e.g. Clone/Edit opens on a different destination. User-driven
    // Buyer changes call setBuyerLabel directly from onChange, so
    // this effect only fires on parent-driven prop updates.
    setBuyerLabel(editingClientName || "");
  }, [editingClientName]);

  return (
    <form onSubmit={onSubmit} className="brut-card p-5 mb-4 grid grid-cols-2 gap-4" data-testid="dest-form">
      {mode === "edit" && (
        <div className="col-span-2 border-2 border-black bg-amber-50 p-3 -mt-1 flex items-center flex-wrap gap-2"
             data-testid="dest-edit-banner">
          <span className="label-mono">Edit destination —</span>
          <span className="font-bold text-sm truncate">{editingName || editingId}</span>
          <span className="ml-auto text-[10px] uppercase tracking-widest text-zinc-500">
            Client &amp; type are read-only during edit
          </span>
        </div>
      )}
      <div>
        <label className="label-mono">Client (blank = platform-wide buyer)</label>
        {/* Iter-61.4.1 — server-searchable Buyer combobox so admins
            can select ANY authorized Client without a 100-row cap.
            Iter-61.4.3 — changing the Buyer in create mode clears
            campaign_ids so Campaigns owned by the previous Buyer
            never carry over into the new Buyer's destination.
            Edit mode is immutable (Iter-61.3) so this branch is
            only hit on Create/Clone. */}
        <BuyerCombobox
          value={form.client_id ? { id: form.client_id, name: buyerLabel } : null}
          onChange={(v) => {
            const nextId = v?.id || "";
            const nextName = v?.name || "";
            // Iter-62.1.3 — track the picked name locally so the visible
            // label always mirrors the current `form.client_id`.
            if (mode !== "edit") setBuyerLabel(nextName);
            setForm((prev) => {
              if (mode !== "edit" && nextId !== prev.client_id) {
                return { ...prev, client_id: nextId, campaign_ids: [] };
              }
              return { ...prev, client_id: nextId };
            });
          }}
          disabled={mode === "edit"}
          includeAll={false}
          placeholder="— platform-wide (super-admin only) —"
          testIdPrefix="dest-client"
          initialName={buyerLabel}
        />
      </div>

      <div>
        <label className="label-mono">Name</label>
        <input data-testid="dest-name" className="w-full border border-black px-3 py-2 mt-1"
          value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} />
      </div>

      <div className="col-span-2">
        <label className="label-mono">
          Type {mode === "edit" && <span className="text-zinc-500 normal-case tracking-normal">— locked in edit (delete + recreate to change type)</span>}
        </label>
        <div className={`grid grid-cols-3 md:grid-cols-3 lg:grid-cols-4 gap-1 mt-1 ${mode === "edit" ? "opacity-70 pointer-events-none" : ""}`} data-testid="dest-type">
          {Object.entries(TYPE_META).map(([v, m]) => {
            const Icon = m.Icon;
            const active = form.type === v;
            return (
              <button type="button" key={v} data-testid={`dest-type-${v}`}
                onClick={() => mode === "edit" ? null : setForm((prev) => ({ ...prev, type: v }))}
                disabled={mode === "edit"}
                className={`px-3 py-2 text-xs uppercase tracking-widest border border-black inline-flex flex-col items-start gap-1 justify-center ${active ? "bg-black text-white" : "hover:bg-zinc-100"}`}>
                <span className="inline-flex items-center gap-2 font-bold">
                  <Icon size={12} weight="bold" /> {m.label}
                </span>
                <span className={`text-[9px] tracking-normal normal-case ${active ? "text-zinc-300" : "text-zinc-500"}`}>
                  {m.help}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label className="label-mono">Max attempts</label>
        <input type="number" className="w-full border border-black px-3 py-2 mt-1"
          value={form.retry_policy.max_attempts}
          onChange={(e) => setForm((prev) => ({ ...prev, retry_policy: { max_attempts: Number(e.target.value) } }))} />
      </div>

      {/* Iter-74.2 — buyer economics. Both are OPTIONAL; empty leaves the
          existing value untouched so legacy destinations aren't disturbed.
          Readiness (`landing_readiness.py`) reads these at top-level. */}
      <div>
        <label className="label-mono">Buyer price (per accepted lead, USD)</label>
        <input
          type="number" min="0" step="0.01"
          className="w-full border border-black px-3 py-2 mt-1"
          value={form.buyer_price ?? ""}
          onChange={(e) => setForm((prev) => ({
            ...prev,
            buyer_price: e.target.value === "" ? null : Number(e.target.value),
          }))}
          placeholder="e.g. 350"
          data-testid="dest-buyer-price"
        />
        <div className="text-[10px] font-mono text-zinc-500 mt-1">
          Clears the "buyer price missing" readiness warning + drives
          auto-invoicing for accepted leads.
        </div>
      </div>
      <div>
        <label className="label-mono">Return window (hours)</label>
        <input
          type="number" min="0" step="1"
          className="w-full border border-black px-3 py-2 mt-1"
          value={form.return_window_hours ?? ""}
          onChange={(e) => setForm((prev) => ({
            ...prev,
            return_window_hours: e.target.value === "" ? null : Number(e.target.value),
          }))}
          placeholder="e.g. 72"
          data-testid="dest-return-window"
        />
        <div className="text-[10px] font-mono text-zinc-500 mt-1">
          How long the buyer has to dispute a lead. Clears the "no
          return window" readiness warning.
        </div>
      </div>

      <TypeSpecificFields type={form.type} config={form.config} setConfig={setConfig} />

      {/* Iter-61 — shared state selector across every destination type.
          Legacy comma-separated `config.state_allowlist` values load
          correctly via `normalizeStateInput`. */}
      <StateAllowlistFields
        config={form.config}
        setConfig={setConfig}
        buyerAcceptedStates={buyerAcceptedStates}
      />

      <div className="col-span-2">
        <label className="label-mono">Campaign filter (empty = all campaigns for this client)</label>
        {/* Iter-61.4.2 — server-searchable Campaign picker scoped to
            the currently-selected Buyer. Unknown ids on Edit-load
            are batch-resolved via POST /campaigns/resolve so a
            Campaign outside the current page never disappears. */}
        <CampaignPicker
          value={form.campaign_ids || []}
          onChange={(ids) => setForm((prev) => ({ ...prev, campaign_ids: ids }))}
          clientId={form.client_id}
          testIdPrefix="dest-campaigns"
        />
      </div>

      {/* Iter-66 — per-destination affiliate blocklist. Lets admins
          remove a specific affiliate from a specific campaign/destination
          without pausing the whole affiliate or the destination. */}
      <div className="col-span-2">
        <label className="label-mono">
          Blocked affiliates (empty = every affiliate can route to this destination)
        </label>
        <div className="text-[10px] text-zinc-500 leading-relaxed mt-0.5 mb-1">
          Leads from any affiliate in this list are skipped for THIS destination
          only — the affiliate can still push to other campaigns.
        </div>
        <AffiliateMultiSelect
          value={form.blocked_affiliate_ids || []}
          onChange={(ids) => setForm((prev) => ({ ...prev, blocked_affiliate_ids: ids }))}
          testIdPrefix="dest-blocked-affiliates"
        />
      </div>

      {mode === "edit" && notify && setNotify && (
        <div className="col-span-2">
          <NotifyBuyerBox value={notify} onChange={setNotify} testId="dest-notify-buyer" preview={notifyPreview} />
        </div>
      )}
      <div className="col-span-2 flex justify-end gap-2">
        {mode === "edit" && onCancel && (
          <button type="button" onClick={onCancel} data-testid="dest-cancel"
            className="border border-black px-4 py-2 uppercase text-xs tracking-widest hover:bg-black hover:text-white">
            Cancel
          </button>
        )}
        <button type="submit" data-testid="dest-submit"
          className="bg-black text-white border border-black px-4 py-2 uppercase text-xs tracking-widest font-bold">
          {mode === "edit" ? (
            <>Save Changes</>
          ) : (
            <><Plus size={12} className="inline mr-1" /> Create destination</>
          )}
        </button>
      </div>
    </form>
  );
}
